import type { ProgramMembershipFragment } from 'bundles/program-home/queries/__generated__/ProgramMembershipsQuery';

// Internal membership states (not exported)
const PROGRAM_MEMBERSHIP_STATE_ALLOWLISTED = 'PROGRAM_MEMBERSHIP_STATE_ALLOWLISTED';
const PROGRAM_MEMBERSHIP_STATE_DELETED_MEMBER = 'PROGRAM_MEMBERSHIP_STATE_DELETED_MEMBER';
const PROGRAM_MEMBERSHIP_STATE_INVALID = 'PROGRAM_MEMBERSHIP_STATE_INVALID';
const PROGRAM_MEMBERSHIP_STATE_INVITED = 'PROGRAM_MEMBERSHIP_STATE_INVITED';
const PROGRAM_MEMBERSHIP_STATE_MEMBER = 'PROGRAM_MEMBERSHIP_STATE_MEMBER';
const PROGRAM_MEMBERSHIP_STATE_SOFT_DELETED_MEMBER = 'PROGRAM_MEMBERSHIP_STATE_SOFT_DELETED_MEMBER';

export class ProgramMembership {
  public readonly id: string;

  public readonly createdAt: string;

  public readonly emailVerified: boolean;

  public readonly userData: {
    fullName: string;
    email: string;
  };

  private readonly state: string;

  constructor(fragment?: ProgramMembershipFragment | null) {
    if (fragment) {
      this.id = fragment.id;
      this.createdAt = fragment.createdAt || '';
      this.state = fragment.state || '';
      this.emailVerified = fragment.emailVerified || false;
      this.userData = {
        fullName: fragment.userData.fullName || '',
        email: fragment.userData.email || '',
      };
    } else {
      // Create invalid membership when no data is provided
      this.id = '';
      this.createdAt = '';
      this.state = PROGRAM_MEMBERSHIP_STATE_INVALID;
      this.emailVerified = false;
      this.userData = {
        fullName: '',
        email: '',
      };
    }
  }

  get isMember(): boolean {
    return this.state === PROGRAM_MEMBERSHIP_STATE_MEMBER;
  }

  get isNotProgramMember(): boolean {
    return this.state === PROGRAM_MEMBERSHIP_STATE_INVALID || this.state === PROGRAM_MEMBERSHIP_STATE_DELETED_MEMBER;
  }

  get isInvited(): boolean {
    return this.state === PROGRAM_MEMBERSHIP_STATE_INVITED;
  }

  get isEmailNotVerified(): boolean {
    return (
      !this.emailVerified &&
      (this.state === PROGRAM_MEMBERSHIP_STATE_INVITED || this.state === PROGRAM_MEMBERSHIP_STATE_ALLOWLISTED)
    );
  }

  get isAllowlisted(): boolean {
    return this.state === PROGRAM_MEMBERSHIP_STATE_ALLOWLISTED;
  }

  get isSoftDeletedMember(): boolean {
    return this.state === PROGRAM_MEMBERSHIP_STATE_SOFT_DELETED_MEMBER;
  }

  get isProgramMember(): boolean {
    return this.state === PROGRAM_MEMBERSHIP_STATE_MEMBER;
  }

  get hasModalToDisplay(): boolean {
    return (
      this.isNotProgramMember ||
      this.isEmailNotVerified ||
      this.isInvited ||
      this.isAllowlisted ||
      this.isSoftDeletedMember
    );
  }

  get isEligibleToJoinProgram(): boolean {
    return (this.isInvited || this.isAllowlisted || this.isSoftDeletedMember) && !this.isEmailNotVerified;
  }

  get email(): string {
    return this.userData.email || '';
  }

  get fullName(): string {
    return this.userData.fullName || '';
  }

  get externalUserData() {
    return {
      definition: {
        email: this.userData.email,
        fullName: this.userData.fullName,
      },
    };
  }
}

export function newProgramMembershipInstance(
  fragment: ProgramMembershipFragment | undefined | null
): ProgramMembership {
  return new ProgramMembership(fragment);
}

export type ProgramMembershipType = ProgramMembership;
